import moment from "moment";
import {
  decimalPlaces,
  getRoundedValue,
  roundMealValue,
  UNKNOWN_NUTRIENT_VALUE,
} from "../../../helpers/mealsHelper";
import { getBaseDateFormat } from "../../../modules/constants";

export const headers24hRecall = {
  date: "Date",
  time: "Time",
  meal: "Meal",
  recall: "24hr Recall",
  paperRecapture: "Paper Recapture",
  calories: "Calories (kcal)",
  totalFat: "Total Fat (g)",
  longChainFat: "Long Chain Fat (g)",
  medChainFat: "Medium Chain Fat (g)",
  protein: "Protein (g)",
  carbohydrates: "Carbohydrates (g)",
  fiber: "Fiber (g)",
  linoleic: "Linoleic Acid (g)",
  linolenic: "Linolenic Acid (g)",
  ala: "ALA (g)",
  dha: "DHA (g)",
  vitA: "Vitamin A (IU)",
  vitD: "Vitamin D (IU)",
  vitE: "Vitamin E (mg)",
  vitEiU: "Vitamin E (IU)",
  vitK: "Vitamin K (mcg)",
  iron: "Iron (mg)",
  zinc: "Zinc (mg)",
  calcium: "Calcium (mg)",
  potassium: "Potassium (mg)",
  sodium: "Sodium (mg)",
  totalSugar: "Sugars - total (g)",
  addedSugars: "Sugars - added (g)",
  choline: "Choline - total (mg)",
  copper: "Copper (mg)",
  folic: "Folic Acid (mcg)",
  magnesium: "Magnesium (mg)",
  manganese: "Manganese (mg)",
  niacin: "Niacin (mg)",
  phosphorus: "Phosphorus (mg)",
  riboflavin: "Riboflavin (mg)",
  selenium: "Selenium (mcg)",
  thiamin: "Thiamin (mg)",
  vitB12: "Vitamin B-12 (mcg)",
  vitB6: "Vitamin B-6 (mg)",
  vitC: "Vitamin C - total ascorbic acid (mg)",
  lCarnitine: "L-Carnitine (mg)",
};

const getMealDescription = (meal) => {
  const itemTitles = meal.items.map((item) => {
    return item.title;
  });

  const description = itemTitles.filter((title) => title !== null).join(" - ");

  return description.replace(/,/g, "-");
};

const aggregateByDay = (data) => {
  const dataByDay = data
    .filter((meal) => meal.date === data[data.length - 1].date)
    .reduce(aggregatorByDay, {});

  return dataByDay;
};

export function getDataTo24HRecallReport(entries) {
  const finalData = [];

  entries.forEach((dailyEntry) => {
    dailyEntry.meals.forEach((meal) => {
      if (!meal.recall) {
        return;
      }

      const mealParsed = JSON.parse(meal.mealJson);

      const mealDate = moment(meal.mealDate).format(getBaseDateFormat());

      if (
        finalData.length > 0 &&
        finalData[finalData.length - 1].date !== mealDate
      ) {
        finalData.push(aggregateByDay(finalData));
      }

      const newMeal = {
        date: mealDate,
        time: moment(meal.mealDate).format("hh:mm A"),
        meal: getMealDescription(mealParsed),
        recall: meal.recall ? "Y" : "",
        paperRecapture: meal.paperRecapture ? "Y" : "",
        ...getRoundedValuesWithMatchedKeys(mealParsed.aboutMeal),
      };

      finalData.push(newMeal);
    });
  });

  finalData.push(aggregateByDay(finalData));

  return finalData;
}

export const parseDailyTargetHistoryData = (dailyTargetHistory) => {
  const finalData = dailyTargetHistory.map((item) => {
    return {
      date: moment
        .utc(item.updatedAt)
        .utcOffset(item.timeZoneOffset)
        .format(getBaseDateFormat()),
      calories: Number(item.calories || 0).toFixed(decimalPlaces),
      longChainFatsPercentage: Number(
        item.longChainFatsPercentage || 0
      ).toFixed(decimalPlaces),
      mediumChainFatsPercentage: Number(
        item.mediumChainFatsPercentage || 0
      ).toFixed(decimalPlaces),
      proteinPercentage: Number(item.proteinPercentage || 0).toFixed(
        decimalPlaces
      ),
      carbohydratesPercentage: Number(
        item.carbohydratesPercentage || 0
      ).toFixed(decimalPlaces),
    };
  });

  return finalData;
};

const matchParams = {
  calories: "Calories",
  totalFat: "Fats",
  longChainFat: "LongChainFats",
  medChainFat: "MediumChainFats",
  protein: "Protein",
  carbohydrates: "Carbs",
  fiber: "Fiber",
  linoleic: "LinoleicAcid",
  linolenic: "LinolenicAcid",
  ala: "ALA",
  dha: "DHA",
  vitA: "VitaminA",
  vitD: "VitaminD",
  vitE: "VitaminE",
  vitEiU: "VitaminE_IU",
  vitK: "VitaminK",
  iron: "Iron",
  zinc: "Zinc",
  calcium: "Calcium",
  potassium: "Potassium",
  sodium: "Sodium",
  totalSugar: "SugarsTotal",
  addedSugars: "SugarsAdded",
  choline: "CholineTotal",
  copper: "Copper",
  folic: "FolicAcid",
  magnesium: "Magnesium",
  manganese: "Manganese",
  niacin: "Niacin",
  phosphorus: "Phosphorus",
  riboflavin: "Riboflavin",
  selenium: "Selenium",
  thiamin: "Thiamin",
  vitB12: "VitaminB12",
  vitB6: "VitaminB6",
  vitC: "VitaminC_TotalAscorbicAcid",
  lCarnitine: "L_Carnitine",
};

const getRoundedValuesWithMatchedKeys = (aboutMeal) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] =
      aboutMeal[matchParams[key]]?.value >= 0
        ? getRoundedValue(aboutMeal[matchParams[key]].value)
        : UNKNOWN_NUTRIENT_VALUE;
  });
  return newObj;
};

const getObjectWithMatchedKeys = (accumulator, item) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    if (
      (accumulator[key] === UNKNOWN_NUTRIENT_VALUE &&
        item[key] === UNKNOWN_NUTRIENT_VALUE) ||
      (accumulator[key] === UNKNOWN_NUTRIENT_VALUE && !item[key]) ||
      (!accumulator[key] && item[key] === UNKNOWN_NUTRIENT_VALUE)
    ) {
      newObj[key] = UNKNOWN_NUTRIENT_VALUE;
    } else {
      const accumulatorValue = Number(
        accumulator[key] === UNKNOWN_NUTRIENT_VALUE || !accumulator[key]
          ? 0
          : accumulator[key]
      );
      const itemValue = Number(
        item[key] === UNKNOWN_NUTRIENT_VALUE || !item[key] ? 0 : item[key]
      );

      newObj[key] = `${Number(
        roundMealValue(accumulatorValue + itemValue)
      ).toFixed(decimalPlaces)}`;
    }
  });

  return newObj;
};

const aggregatorByDay = (accumulator, item) => {
  return {
    date: `${item.date} Total`,
    time: "",
    meal: "",
    recall: "",
    paperRecapture: "",
    ...getObjectWithMatchedKeys(accumulator, item),
  };
};
